import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvent, AnalyticsBloc, AnalyticsPageName } from '@kanalytics';
import { SettingsBloc, TagBloc } from '@kbloc';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { DataLinkBloc } from '@kp/data-link/data-link.bloc';
import { PulseSurveyBloc } from '@kp/pulse-survey/pulse-survey.bloc';
import { BrowserCheckService } from '@kp/shared/services/browser-check.service';
import { ReturnUrlService } from '@kp/shared/services/return-url.service';
import { BrowserStorage, DataStoreService, OnboardingUtilities, TitleService } from '@kservice';
import { HttpStatusCode } from '@ktypes/enums';
import { Credentials, Setting, Status } from '@ktypes/models';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-screen-login',
  templateUrl: './screen-login.component.html',
  styleUrls: ['./screen-login.component.scss'],
})
export class ScreenLoginComponent implements OnInit, OnDestroy {
  email: UntypedFormControl;
  loginDisabled = false;
  loginError = false;
  loginErrorCopy = 'The email and password you entered do not match our records. Please try again.';
  loginForm: UntypedFormGroup;
  showEmailVerificationModal = false;
  showPassword = false;
  showPasswordResetModal = false;
  stayLoggedIn = true;

  private _destroy$ = new Subject<void>();
  private _password: UntypedFormControl;
  private _redirectUrl: string;
  private _submitClicked = false;
  private _title = 'Login';

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _analyticsBloc: AnalyticsBloc,
    private _authenticationBloc: AuthenticationBloc,
    private _browserCheckService: BrowserCheckService,
    private _browserStorage: BrowserStorage,
    private _dataLinkBloc: DataLinkBloc,
    private _dataStoreService: DataStoreService,
    private _onboardingUtil: OnboardingUtilities,
    private _pulseSurveyBloc: PulseSurveyBloc,
    private _returnUrlService: ReturnUrlService,
    private _router: Router,
    private _settingsBloc: SettingsBloc,
    private _tagBloc: TagBloc,
    private _titleService: TitleService
  ) {}

  ngOnInit() {
    const { queryParams } = this._activatedRoute.snapshot;
    this._redirectUrl = queryParams['redirectUrl'];
    this._browserStorage.setObject('stayLoggedIn', this.stayLoggedIn, 30, true);
    this.email = new UntypedFormControl(this.email, [Validators.required, Validators.pattern(/^.+@.+$/)]);
    this._password = new UntypedFormControl(this._password, [Validators.required]);
    this._titleService.set(this._title);
    this.loginForm = new UntypedFormGroup({
      email: this.email,
      password: this._password,
    });
    this._settingsBloc.changeUserSetting(Setting.staySignedIn, this.stayLoggedIn);
    this._dataStoreService.authStatus$.pipe(takeUntil(this._destroy$)).subscribe(
      (status) => {
        // Initial state - do nothing
        if (!status || !this._submitClicked) {
          return;
        }
        switch (status.status) {
          case Status.starting: // 0
            // Show busy indicator
            this.loginDisabled = true;
            break;
          case Status.local: // 1
          case Status.done: // 2
            if (status.data && status.data.user) {
              this.loginError = false;
              this.loginDisabled = false;

              const loggedInEvent = this._analyticsBloc.createEventFromInteraction({
                page: AnalyticsPageName.login,
                event: AnalyticEvent.logged_in,
              });
              this._analyticsBloc.sendEvent(loggedInEvent);

              this._advanceToNextScreen();
            } else {
              // Do nothing, user likely came through the onboarding flow
              // and clicked on a "Sign In" link after realizing they already
              // had an account. Meanwhile, a guest user has already been created
              // which sets an AuthData object (containing the tokens) but with a
              // null user reference.
            }
            break;
          case Status.error: // 3
            // Show error message and stay on this page
            if (
              status.message?.code === HttpStatusCode.FORBIDDEN &&
              status.message?.message === 'PasswordResetRequiredException'
            ) {
              this.showPasswordResetModal = true;
              this.email.disable();
              this._password.disable();
            } else if (
              status.message?.code === HttpStatusCode.BAD_REQUEST &&
              status.message?.message === 'EmailVerificationRequired'
            ) {
              this.showEmailVerificationModal = true;
              this.email.disable();
              this._password.disable();
            } else {
              this.loginError = true;
              this.loginDisabled = false;
            }
            break;
        }
      },
      (error) => {
        console.warn('onLogin error: ', error);
        this.loginError = true;
        this.loginDisabled = false;
        if (error && error === 'Invalid username/password combination') {
          this.loginErrorCopy = error;
        }
      }
    );
  }

  ngOnDestroy() {
    this.email = null;
    this._password = null;
    this._destroy$.next();
    this._destroy$.complete();
  }

  emailChanged() {
    this.loginError = false;
    this.loginDisabled = false;
  }

  passwordChanged() {
    this.loginError = false;
    this.loginDisabled = false;
  }

  sendCode() {
    if (this.email.valid) {
      this._authenticationBloc.proposedResetEmail = this.email.value;
    }
  }

  onSubmit(formValues: Credentials) {
    if (this.loginForm.valid) {
      this.loginDisabled = true;
      this._submitClicked = true;
      // clear any existing Payment Required Error Messages, then attempt login
      this._authenticationBloc.resetPaymentRequiredErrorMessage();
      this._dataLinkBloc.clearLinkData();
      this._authenticationBloc.login(new Credentials(formValues.email, formValues.password));
    } else {
      this.loginDisabled = false;
    }
  }

  toggleShowPassword(event: MouseEvent) {
    // Make sure the button won't submit the form
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.showPassword = !this.showPassword;
  }

  loggedInCheckbox(isChecked: boolean) {
    this.stayLoggedIn = isChecked;
    this._settingsBloc.changeUserSetting(Setting.staySignedIn, this.stayLoggedIn);
  }

  private _advanceToNextScreen() {
    // clean up localStorage re: onboarding
    this._onboardingUtil.removeOnboardingData();

    // prep for and advance to the next page
    this._pulseSurveyBloc.allowPulseSurveyCheckToday(true);

    this._settingsBloc.getUserSettings();

    if (!this._browserCheckService.checkBrowser()) {
      this._router.navigate(['/help'], { replaceUrl: true });
    } else {
      this._tagBloc.getUserTags();
      // Move to initial app screen
      const redirectRoute = (this._redirectUrl && decodeURIComponent(this._redirectUrl)) || '';
      this._returnUrlService.setSavedUrl(this._redirectUrl);
      this._router.navigateByUrl(redirectRoute, { replaceUrl: true });
    }
  }

  isEmailVerificationModalOpen() {
    this.showEmailVerificationModal = false;
    this.email.enable();
    this._password.enable();
  }

  isPasswordResetModalOpen() {
    this.showPasswordResetModal = false;
    this.email.enable();
    this._password.enable();
  }
}
